import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import AdminPage from '../../components/admin/AdminPage';
import SectionCard from '../../components/admin/SectionCard';
import { LoadingState } from '../../components/admin/StateViews';
import { AdminSettings } from '../../types';
import { AdminSettingsService } from '../../services/AdminSettingsService';
import { AuditLogService } from '../../services/AuditLogService';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../hooks/useAuth';

const SettingsPage = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    AdminSettingsService.getSettings().then(setSettings);
  }, []);

  if (!settings) {
    return <LoadingState label="Loading admin settings…" />;
  }

  const handleSave = async () => {
    setSaving(true);
    await AdminSettingsService.saveSettings(settings);
    await AuditLogService.recordEntry({
      module: 'settings',
      action: 'settings_saved',
      description: 'Admin settings were updated.',
      actorEmail: user?.email || 'admin@local',
    });
    showToast('Settings saved successfully.');
    setSaving(false);
  };

  return (
    <AdminPage
      title="Settings"
      description="General configuration, defaults, notifications, and admin preferences."
      action={
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      }
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="General settings"
            description="These defaults shape how the admin workspace behaves."
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="App name"
                  fullWidth
                  value={settings.appName}
                  onChange={(event) =>
                    setSettings({ ...settings, appName: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Support email"
                  fullWidth
                  value={settings.supportEmail}
                  onChange={(event) =>
                    setSettings({ ...settings, supportEmail: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Default cause category"
                  fullWidth
                  value={settings.defaultCauseCategory}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      defaultCauseCategory: event.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Notifications & workflow"
            description="Operational controls that guide publishing and admin preferences."
          >
            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notificationsEnabled}
                    onChange={(event) =>
                      setSettings({
                        ...settings,
                        notificationsEnabled: event.target.checked,
                      })
                    }
                  />
                }
                label="Enable admin notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.approvalRequiredForPublishing}
                    onChange={(event) =>
                      setSettings({
                        ...settings,
                        approvalRequiredForPublishing: event.target.checked,
                      })
                    }
                  />
                }
                label="Require publishing approval"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.adminPreferences.compactTables}
                    onChange={(event) =>
                      setSettings({
                        ...settings,
                        adminPreferences: {
                          ...settings.adminPreferences,
                          compactTables: event.target.checked,
                        },
                      })
                    }
                  />
                }
                label="Use compact tables"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.adminPreferences.showQuickActions}
                    onChange={(event) =>
                      setSettings({
                        ...settings,
                        adminPreferences: {
                          ...settings.adminPreferences,
                          showQuickActions: event.target.checked,
                        },
                      })
                    }
                  />
                }
                label="Show dashboard quick actions"
              />
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>
    </AdminPage>
  );
};

export default SettingsPage;
